import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider, Select, Tooltip, Typography } from "antd";

import { WHITE } from "../constant/colors";

const commonStyle = {
  width: "100%",
  border: `0px`,
  padding: 0
};

const { Text } = Typography;
const formatTooltipContent = (id, tooltipContent) => {
  if (!tooltipContent || tooltipContent.length === 0) {
    return "";
  }

  return tooltipContent
    .map((item) => {
      return item.text || item.children;
    })
    .join(" | ");
};

const MultiSelect = ({
  children,
  maxTagCount = 1,
  maxTagTextLength,
  selectAll = false,
  clearAll = false,
  useCommonStyle = true,
  onClearAll,
  onSelectAll,
  style = {},
  id,
  bordered = false,
  tooltipContent,
  placeholder,
  tooltipWidth = "40vw",
  ...props
}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isHovering, setHovering] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const formattedTooltipContent = formatTooltipContent(id, tooltipContent);

  return (
    <div id={id}>
      <Tooltip
        open={formattedTooltipContent && !isDropdownOpen && isHovering}
        title={<Text>{formattedTooltipContent}</Text>}
        color={WHITE}
        placement="right"
        overlayStyle={{
          maxWidth: tooltipWidth,
          whiteSpace: "normal",
          wordWrap: "break-word"
        }}
      >
        <Select
          {...props}
          mode="multiple"
          bordered={bordered}
          showArrow={true}
          allowClear={true}
          onClear={() => {
            onClearAll();
            setTimeout(() => {
              document.activeElement.blur();
            }, 0);
          }}
          maxTagCount={maxTagCount}
          maxTagTextLength={maxTagTextLength}
          placeholder={isFocused || isDropdownOpen ? placeholder : t("all")}
          getPopupContainer={(trigger) => trigger.parentNode}
          onDropdownVisibleChange={(open) => {
            setDropdownOpen(open);
          }}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={{
            ...(useCommonStyle && commonStyle),
            ...style,
            padding: props.label ? "10px 0 0 0" : ""
          }}
          dropdownRender={(menu) => (
            <div>
              {selectAll && clearAll && (
                <>
                  <div style={{ display: "flex", flexWrap: "nowrap", padding: 8, gap: 6 }}>
                    <Button block onClick={onSelectAll}>
                      {t("select_all")}
                    </Button>
                    <Button onClick={onClearAll}>{t("clear")}</Button>
                  </div>

                  <Divider style={{ margin: "4px 0" }} />
                </>
              )}
              {menu}
            </div>
          )}
        >
          {children}
        </Select>
      </Tooltip>
    </div>
  );
};

const SingleSelect = ({
  children,
  required = false,
  showSearch = true,
  bordered = false,
  useCommonStyle = true,
  style,
  id,
  ...props
}) => {
  return (
    <div id={id}>
      <Select
        bordered={bordered}
        showSearch={showSearch}
        style={{
          ...(useCommonStyle && commonStyle),
          padding: props.label ? "6px 0 0 0" : "",
          ...style
        }}
        placeholder={props.placeholder && (required ? `* ${props.placeholder}` : props.placeholder)}
        getPopupContainer={(trigger) => trigger.parentNode}
        {...props}
      >
        {children}
      </Select>
    </div>
  );
};

export { SingleSelect, MultiSelect };
